import { OrderedSet } from "immutable";

/**
 * Selection state interface.
 */
export interface ISelectionState<T = string> {
    allSelected: boolean;
    size: number;
    isFinite: boolean;
    add(item: T): ISelectionState<T>;
    bulkAdd(items: T[]): ISelectionState<T>;
    delete(item: T): ISelectionState<T>;
    includes(item: T): boolean;
    toFilters(fn: (item: T) => any, field: string): object;
    toArray(): T[];
    isEmptyList(): boolean;
    first(): T;
}

/**
 * Implements classical state of items selection.
 * Collects id's of all selected items.
 */
export class SelectionWhiteList<T = string> implements ISelectionState<T> {
    constructor(
        private list: OrderedSet<T> = OrderedSet<T>(),
        private limit: number = null,
    ) {}

    get allSelected() {
        return this.isFinite && this.limit === this.size;
    }

    get size(): number {
        return this.list.size;
    }

    get isFinite(): boolean {
        return !!this.limit;
    }

    isEmptyList(): boolean {
        return this.list.size === 0;
    }

    add(item: T) {
        return new SelectionWhiteList<T>(this.list.add(item), this.limit);
    }

    bulkAdd(items: T[]) {
        return new SelectionWhiteList<T>(this.list.union(items), this.limit);
    }

    delete(item: T) {
        return new SelectionWhiteList<T>(this.list.delete(item), this.limit);
    }

    includes(item: T) {
        return this.list.includes(item);
    }

    toFilters(fn = (id: T) => id, field = "_id") {
        return {
            [field]: this.list.map(fn).toJS(),
        };
    }

    toArray() {
        return this.list.toArray();
    }

    first() {
        return this.list.first();
    }
}

/**
 * Implements selection state of all items except several ones.
 * Collects id's of items which excluded from selection.
 */
export class SelectionBlackList<T = string> implements ISelectionState<T> {
    constructor(private list: OrderedSet<T> = OrderedSet<T>()) {}

    get allSelected() {
        return true;
    }

    get size(): number {
        return Infinity;
    }

    get isFinite(): boolean {
        return false;
    }

    isEmptyList(): boolean {
        return this.list.size === 0;
    }

    add(item: T) {
        return new SelectionBlackList<T>(this.list.delete(item));
    }

    bulkAdd(items: T[]) {
        return new SelectionBlackList<T>(this.list.subtract(items));
    }

    delete(item: T) {
        return new SelectionBlackList<T>(this.list.add(item));
    }

    includes(item: T) {
        return !this.list.includes(item);
    }

    toFilters(fn = (id: T) => id, field = "_id") {
        return {
            [`${field}_excluded`]: this.list.map(fn).toJS(),
        };
    }

    toArray() {
        return this.list.toArray();
    }

    first() {
        return this.list.first();
    }
}
